import React from 'react';
import clsx from 'clsx';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';

interface BatteryLevelManagerProps {
  user: BatteryLevel;
  avgCustomer: BatteryLevel;
}

interface BatteryLevel {
  start: number;
  end: number;
}

function SegmentGraph({user, avgCustomer}: BatteryLevelManagerProps): JSX.Element {
  const {t} = useInferableTranslation();
  const {segments, startIndex, endIndex} = calculateSegments(user.start, user.end);
  const avgCustomerWidth = getCalculatedWidth(avgCustomer.start, avgCustomer.end);
  const avgCustomerStartPosition = getStartPosition(avgCustomer.start);

  return (
    <>
      <div className="battery_graph_area">
        <div className="block_graph_box">
          <div
            className="average_line"
            style={{width: avgCustomerWidth, transform: 'initial', left: avgCustomerStartPosition}}
          >
            <div className="line left">
              <span className="arrow left"></span>
              <span className="dotted"></span>
            </div>
            <span className="customer_average">{t('T30429')}</span>
            <div className="line right">
              <span className="dotted"></span>
              <span className="arrow right"></span>
            </div>
          </div>
          {segments.map((segment, index) => (
            <div
              className={clsx('block_item', {'is-active': !!segment})}
              key={index}
            >
              <div
                className="block_bar"
                style={{
                  width: `${segment * 10}%`,
                  ...(endIndex === index && {marginRight: 'auto', marginLeft: 'initial'}),
                  ...(startIndex === index && {marginRight: 'initial', marginLeft: 'auto'}),
                }}
              ></div>
            </div>
          ))}
        </div>
      </div>
      <div className="gauge_guide">
        <span className="gauge_text">0%</span>
        <span className="gauge_text">100%</span>
      </div>
    </>
  );
}

export default SegmentGraph;

function calculateSegments(start: number, end: number) {
  // 0부터 100까지 10개의 조각 크기 계산
  const segments: number[] = Array(10).fill(0);
  const segmentSize = 10; // 각 조각의 크기 (0-10, 10-20, ..., 90-100)

  // 시작과 끝 값의 조각 인덱스 계산
  const startIndex = Math.floor(start / segmentSize);
  const endIndex = Math.floor(end / segmentSize);

  // 각 조각에 값 할당
  for (let i = 0; i < segments.length; i++) {
    if (i < startIndex) {
      segments[i] = 0; // 시작 전 조각
    } else if (i > endIndex) {
      segments[i] = 0; // 끝 이후 조각
    } else {
      segments[i] = i === startIndex ? 10 - (start % segmentSize) : i === endIndex ? end % segmentSize : 10; // 각각의 조각 값 설정
    }
  }

  return {
    segments,
    startIndex,
    endIndex,
  };
}

function getCalculatedWidth(start: number, end: number) {
  const {segments} = calculateSegments(start, end);
  const segmentCount = segments.filter((segment) => !!segment).length;
  const gapCount = segmentCount - 1;

  return calculateWidth((end - start) / 10, gapCount);
}

function getStartPosition(start: number) {
  const segmentCount = start / 10;
  const gapCount = Math.ceil(start / 10) - 1;
  return calculateWidth(segmentCount, gapCount);
}

function calculateWidth(segmentCount: number, gapCount: number) {
  const segmentWidthPercent = 10; // 각 세그먼트 너비의 퍼센트
  const segmentWidthPx = 7.2; // 각 세그먼트 너비의 픽셀
  // segment 하나당 (10% - 7.2px)
  const gapWidth = 8; // 배열 사이의 간격 (px)
  return `calc(${segmentCount * segmentWidthPercent}% - ${segmentCount * segmentWidthPx}px + ${gapWidth * gapCount}px)`;
}
